import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input, Select, Tooltip } from "@legal-500/legal500-design-system";
import { regionNodeKey, useRegionNodes } from "@/hooks/useRegions";
import { CompanyPicker } from "./CompanyPicker";
import styles from "./ProfilesListFilters.module.css";

export type EnabledFilters = {
  company?: boolean;
  status?: boolean;
  q?: boolean;
  country?: boolean;
  region?: boolean;
};

export type DisabledFilters = {
  company?: boolean;
  status?: boolean;
  q?: boolean;
  country?: boolean;
  region?: boolean;
};

export type ProfilesListFiltersProps = {
  enabledFilters: EnabledFilters;
  /**
   * Per-control disable. Defaults: `country` is disabled with a "Coming soon"
   * tooltip because the profiles-api list endpoints do not yet accept it.
   * `region` is enabled by default — the filter selects a `(kind,id)` node from
   * the `api_region_country_v2` tree and the list endpoints filter via
   * `?regionKind=&regionId=` (§3.4).
   */
  disabled?: DisabledFilters;
  statusOptions?: Array<{ value: string; label: string }>;
  /**
   * Extra filter cells (e.g. Profession, Article type) rendered to the right
   * of the built-ins so each page can plug in its own dropdowns without
   * having to compose a second toolbar row. Cells should be plain `<Select>`
   * / `<Input>` elements wrapped in a div with `data-profiles-filter` so they
   * pick up the same `min-width` constraints visually — see
   * `<ProfilesFilterCell>` below.
   */
  children?: ReactNode;
};

const DEFAULT_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

const Q_DEBOUNCE_MS = 300;

/**
 * Wrapper for additional filter inputs so they share the same min-width and
 * label/select styling as the built-in cells. Use:
 *
 *   <ProfilesListFilters enabledFilters={…}>
 *     <ProfilesFilterCell>
 *       <Select label="Article type" … />
 *     </ProfilesFilterCell>
 *   </ProfilesListFilters>
 */
export function ProfilesFilterCell({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return <div className={wide ? styles.filterWide : styles.filter}>{children}</div>;
}

export function ProfilesListFilters({
  enabledFilters,
  disabled,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  children,
}: ProfilesListFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const companyId = searchParams.get("companyId");
  const status = searchParams.get("status") ?? "";
  const country = searchParams.get("country") ?? "";
  // §3.4: a region filter is `(regionKind, regionId)` together or neither. The
  // select stores its value as the `${kind}:${id}` node key and splits it back
  // into the two URL params on change.
  const regionKind = searchParams.get("regionKind") ?? "";
  const regionIdParam = searchParams.get("regionId") ?? "";
  const regionSelectValue = regionKind && regionIdParam ? `${regionKind}:${regionIdParam}` : "";

  // Profile region/country nodes (`api_region_country_v2`) — fetched once and
  // cached via `useRegionNodes`. We render a flat select for the staff-side
  // filter (tree picking is reserved for the editor surface). Each option's
  // value is the `${kind}:${id}` node key.
  const { nodes: regionNodes, loading: regionsLoading } = useRegionNodes();
  const regionOptions = useMemo(
    () => [
      { value: "", label: "All regions" },
      ...regionNodes.map(n => ({ value: regionNodeKey(n), label: n.name ?? "—" })),
    ],
    [regionNodes],
  );

  // Local q state to allow debouncing.
  const [qLocal, setQLocal] = useState<string>(searchParams.get("q") ?? "");
  useEffect(() => {
    const next = searchParams.get("q") ?? "";
    setQLocal(prev => (prev === next ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("q")]);

  useEffect(() => {
    if (!enabledFilters.q) return;
    const current = searchParams.get("q") ?? "";
    if (qLocal === current) return;
    const timer = setTimeout(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (qLocal) next.set("q", qLocal);
        else next.delete("q");
        return next;
      });
    }, Q_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qLocal, enabledFilters.q]);

  function setParam(key: string, value: string | null) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }

  // The region filter writes `regionKind` + `regionId` together (§3.4). The
  // select value is a `${kind}:${id}` node key; empty clears both params.
  function setRegionFilter(nodeKey: string) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (nodeKey) {
        const [kind, id] = nodeKey.split(":");
        next.set("regionKind", kind);
        next.set("regionId", id);
      } else {
        next.delete("regionKind");
        next.delete("regionId");
      }
      return next;
    });
  }

  // Country defaults to disabled (API doesn't accept it yet); region is wired
  // through staff-bff so we enable it by default.
  const countryDisabled = disabled?.country ?? true;
  const regionDisabled = disabled?.region ?? false;
  const companyDisabled = disabled?.company ?? false;
  const statusDisabled = disabled?.status ?? false;
  const qDisabled = disabled?.q ?? false;

  return (
    <div role="region" aria-label="Profiles filters" className={styles.toolbar}>
      {enabledFilters.company && (
        <div className={styles.filter}>
          <CompanyPicker
            value={companyId}
            disabled={companyDisabled}
            onChange={id => setParam("companyId", id)}
          />
        </div>
      )}

      {enabledFilters.status && (
        <div className={styles.filter}>
          <Select
            label="Status"
            value={status}
            disabled={statusDisabled}
            options={statusOptions}
            onChange={e => setParam("status", e.target.value || null)}
            size="small"
          />
        </div>
      )}

      {enabledFilters.q && (
        <div className={styles.filterWide}>
          <Input
            label="Search"
            type="search"
            value={qLocal}
            disabled={qDisabled}
            placeholder="Search…"
            onChange={e => setQLocal(e.target.value)}
            aria-label="Free-text search"
          />
        </div>
      )}

      {enabledFilters.country && (
        <Tooltip title="Country filter" content={countryDisabled ? "Coming soon" : ""}>
          <div className={styles.filter} data-testid="country-filter-wrapper">
            <Input
              label="Country"
              type="text"
              value={country}
              disabled={countryDisabled}
              placeholder="ISO country code"
              onChange={e => setParam("country", e.target.value || null)}
              aria-label="Country filter"
            />
          </div>
        </Tooltip>
      )}

      {enabledFilters.region && (
        <div className={styles.filter} data-testid="region-filter-wrapper">
          <Select
            label="Region"
            value={regionSelectValue}
            disabled={regionDisabled || regionsLoading}
            options={regionOptions}
            onChange={e => setRegionFilter(e.target.value)}
            aria-label="Region filter"
            size="small"
          />
        </div>
      )}

      {children}
    </div>
  );
}
